// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's edits (frontend-u0o.9, slice S2): the Egenskaber
 * panel's editable fields per element, the `POST /model/edits` ops a field
 * change or a per-field reset makes, Slet and Tilbage til detekteret, and
 * the optimistic overlay that shows a batch before the server has answered.
 *
 * Coordinates are world, under the GET names, so a value read and sent back
 * round-trips (ruxd-5uv). The per-field reset reads `metadata.edited_from`,
 * the detected record the server keeps in the row's **level frame**: only
 * the fields a rigid level frame leaves alone (lengths, heights, thickness,
 * kinds, names) can be reset one by one; a position goes back with
 * Tilbage til detekteret (`*.revert`). Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidOpening, SolidSpace, SolidWall, Storey } from '../api/types';
import { findElement, spaceLabel, type FoundElement, type GeometriModel } from './geometri';
import { formatLength } from './ruler';
import { formatAreaDa } from './spaces';

// ------------------------------------------------------------- numbers --

/**
 * A length typed in Danish or English: "0,12", "0.12 m", "12 cm", "120 mm",
 * "1 200" (a thousands space). Metres; null for anything else.
 */
export function parseLength(text: string): number | null {
  const t = text.trim().toLowerCase().replace(/\s+(?=\d)/g, '');
  const m = /^(-?\d+(?:[.,]\d*)?|-?[.,]\d+)\s*(mm|cm|m)?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1].replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  const unit = m[2] ?? 'm';
  return unit === 'mm' ? n / 1000 : unit === 'cm' ? n / 100 : n;
}

/** A length for a text field: "0,12" (metres, two decimals, no unit). */
export function lengthInput(m: number): string {
  return m.toFixed(2).replace('.', ',');
}

/** Equal within half a millimetre (the model's own rounding). */
const same = (a: number, b: number) => Math.abs(a - b) < 5e-4;

// ------------------------------------------------------- edited_from --

/** The detected / imported record a row was edited from (`metadata.edited_from`), or null. */
export function editedFrom(metadata: unknown): Record<string, unknown> | null {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const from = (metadata as Record<string, unknown>).edited_from;
  return typeof from === 'object' && from !== null && !Array.isArray(from) ? (from as Record<string, unknown>) : null;
}

const num = (r: Record<string, unknown> | null, key: string): number | null => {
  const v = r?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

// -------------------------------------------------------------- fields --

export type FieldKey =
  | 'length'
  | 'thickness'
  | 'height'
  | 'base'
  | 'external'
  | 'width'
  | 'opening_height'
  | 'sill'
  | 'opening_kind'
  | 'name'
  | 'space_kind'
  | 'floor'
  | 'ceiling_height'
  /** Read-only: shown, never written. */
  | 'area';

export interface FieldOption {
  value: string;
  label: string;
}

/** One field of the Egenskaber panel. */
export interface EditField {
  key: FieldKey;
  label: string;
  /** `length`: a number in metres typed as text; `choice`: a segmented control; `text`: free text. */
  type: 'length' | 'choice' | 'text';
  /** For a length: metres (null = unknown); for a choice: the option value; for text: the text. */
  value: number | string | null;
  /** How the value reads, e.g. "0,12 m". */
  display: string;
  options?: FieldOption[];
  /** Differs from what detection or the import made (accent border). */
  edited: boolean;
  /** The detected value, for ↺'s tooltip ("Detekteret: 0,10 m"); null when not resettable. */
  detected: string | null;
  /** A write can change it (false: shown read-only). */
  editable: boolean;
}

export interface PropertySection {
  title: string;
  fields: EditField[];
}

export type ChipTone = 'accent' | 'neutral' | 'success' | 'warning';

export interface PropertiesView {
  guid: string;
  kind: FoundElement['kind'];
  /** "Væg · Inderæg", "Åbning · Dør". */
  eyebrow: string;
  title: string;
  chips: { label: string; tone: ChipTone }[];
  sections: PropertySection[];
  /** Tilbage til detekteret applies (the row was edited from a detected or imported one, or drawn by hand). */
  revertable: boolean;
  /** Slet applies (slabs are derived, never deleted). */
  deletable: boolean;
  related: { guid: string; label: string; role: string }[];
}

const wallLength = (w: Pick<SolidWall, 'start' | 'end'>) => Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);

function storeyOf(model: GeometriModel, id: number | null): Storey | null {
  return id === null ? null : (model.storeys?.find((s) => s.id === id) ?? null);
}

const OPENING_KINDS: FieldOption[] = [
  { value: 'door', label: 'Dør' },
  { value: 'window', label: 'Vindue' },
  { value: 'opening', label: 'Hul' },
];
const SPACE_KINDS: FieldOption[] = [
  { value: 'room', label: 'Rum' },
  { value: 'corridor', label: 'Gang' },
  { value: 'open_plan', label: 'Åben' },
];
const WALL_TYPES: FieldOption[] = [
  { value: 'inner', label: 'Inder' },
  { value: 'outer', label: 'Yder' },
];

function lengthField(key: FieldKey, label: string, value: number | null, detected: number | null, editable = true): EditField {
  return {
    key,
    label,
    type: 'length',
    value,
    display: value === null ? 'ukendt' : formatLength(value),
    edited: detected !== null && (value === null || !same(value, detected)),
    detected: detected === null ? null : formatLength(detected),
    editable,
  };
}

function choiceField(key: FieldKey, label: string, options: FieldOption[], value: string, detected: string | null): EditField {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return {
    key,
    label,
    type: 'choice',
    value,
    display: labelOf(value),
    options,
    edited: detected !== null && detected !== value,
    detected: detected === null ? null : labelOf(detected),
    editable: true,
  };
}

/** The fields of a wall, an opening or a space; null for a slab (derived, not edited in S2). */
export function editFields(model: GeometriModel, found: FoundElement): PropertySection[] {
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      const from = editedFrom(w.metadata);
      const floor = storeyOf(model, w.storey_id)?.z_min ?? 0;
      const fromLen = from && [num(from, 'x0'), num(from, 'y0'), num(from, 'x1'), num(from, 'y1')].every((v) => v !== null)
        ? Math.hypot(num(from, 'x1')! - num(from, 'x0')!, num(from, 'y1')! - num(from, 'y0')!)
        : null;
      const fromThick = num(from, 'thickness');
      const fromExternal = typeof from?.is_external === 'boolean' ? (from.is_external ? 'outer' : 'inner') : null;
      return [
        {
          title: 'Geometri',
          fields: [
            lengthField('length', 'Længde', wallLength(w), fromLen),
            // A thickness of −1 in the record is "unknown".
            lengthField('thickness', 'Tykkelse', w.thickness, fromThick !== null && fromThick > 0 ? fromThick : null),
            lengthField('height', 'Højde', w.z_top - w.z_base, num(from, 'height')),
            lengthField('base', 'Underkant', w.z_base - floor, null),
          ],
        },
        { title: 'Rolle', fields: [choiceField('external', 'Type', WALL_TYPES, w.is_external ? 'outer' : 'inner', fromExternal)] },
      ];
    }
    case 'opening': {
      const o = found.row;
      const from = editedFrom(o.metadata);
      const host = model.walls?.find((w) => w.guid === o.wall_guid) ?? null;
      const s0 = num(from, 's_start');
      const s1 = num(from, 's_end');
      const zs = num(from, 'z_sill');
      const zh = num(from, 'z_head');
      const fromKind = typeof from?.kind === 'string' ? from.kind : null;
      return [
        {
          title: 'Mål',
          fields: [
            lengthField('width', 'Bredde', Math.abs(o.s_end - o.s_start), s0 !== null && s1 !== null ? Math.abs(s1 - s0) : null),
            lengthField('opening_height', 'Højde', o.z_head - o.z_sill, zs !== null && zh !== null ? zh - zs : null),
            lengthField('sill', 'Brystning', o.z_sill - (host?.z_base ?? storeyOf(model, o.storey_id)?.z_min ?? 0), null),
          ],
        },
        { title: 'Type', fields: [choiceField('opening_kind', 'Art', OPENING_KINDS, o.kind, fromKind)] },
      ];
    }
    case 'space': {
      const s = found.row;
      const from = editedFrom(s.metadata);
      const zf = num(from, 'z_floor');
      const zc = num(from, 'z_ceiling');
      const floor = storeyOf(model, s.storey_id)?.z_min ?? 0;
      const fromName = typeof from?.name === 'string' ? from.name : null;
      const fromKind = typeof from?.kind === 'string' ? from.kind : null;
      const kindOptions = SPACE_KINDS.some((k) => k.value === s.kind) ? SPACE_KINDS : [...SPACE_KINDS, { value: s.kind, label: 'Ukendt' }];
      return [
        {
          title: 'Rum',
          fields: [
            {
              key: 'name',
              label: 'Navn',
              type: 'text',
              value: s.name,
              display: spaceLabel(s),
              edited: fromName !== null && fromName !== s.name,
              detected: fromName,
              editable: true,
            },
            choiceField('space_kind', 'Type', kindOptions, s.kind, fromKind),
            ...(s.area !== null
              ? [{ key: 'area' as const, label: 'Areal', type: 'text' as const, value: null, display: formatAreaDa(s.area), edited: false, detected: null, editable: false }]
              : []),
          ],
        },
        {
          title: 'Højder',
          fields: [
            lengthField('floor', 'Gulv', s.z_floor - floor, null),
            lengthField('ceiling_height', 'Loftshøjde', s.z_ceiling - s.z_floor, zf !== null && zc !== null ? zc - zf : null),
          ],
        },
      ];
    }
    case 'slab':
      return [];
  }
}

// ----------------------------------------------------------------- ops --

/**
 * The ops that set field `key` of element `guid` to `value` (metres for a
 * length, the option value for a choice, the text for a name). Null when
 * the value is not valid there (a negative thickness, an empty name, a wall
 * of no length) or nothing would change.
 */
export function fieldOps(model: GeometriModel, guid: string, key: FieldKey, value: number | string): ModelEditOp[] | null {
  const found = findElement(model, guid);
  if (!found) return null;
  const n = typeof value === 'number' ? value : NaN;
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      const floor = storeyOf(model, w.storey_id)?.z_min ?? 0;
      switch (key) {
        case 'length': {
          const L = wallLength(w);
          if (!(n > 0.01) || !(L > 1e-6) || same(n, L)) return null;
          const d = [(w.end[0] - w.start[0]) / L, (w.end[1] - w.start[1]) / L];
          return [{ op: 'wall.update', guid, set: { end: [round(w.start[0] + d[0] * n), round(w.start[1] + d[1] * n)] } }];
        }
        case 'thickness':
          if (!(n > 0.005) || (w.thickness !== null && same(n, w.thickness))) return null;
          return [{ op: 'wall.update', guid, set: { thickness: round(n), assumed: false } }];
        case 'height':
          if (!(n > 0.05) || same(n, w.z_top - w.z_base)) return null;
          return [{ op: 'wall.update', guid, set: { z_top: round(w.z_base + n) } }];
        case 'base': {
          if (!Number.isFinite(n) || same(n, w.z_base - floor)) return null;
          const zBase = floor + n;
          return [{ op: 'wall.update', guid, set: { z_base: round(zBase), z_top: round(zBase + (w.z_top - w.z_base)) } }];
        }
        case 'external': {
          const ext = value === 'outer';
          if (ext === w.is_external) return null;
          return [{ op: 'wall.update', guid, set: { is_external: ext } }];
        }
        default:
          return null;
      }
    }
    case 'opening': {
      const o = found.row;
      const host = model.walls?.find((w) => w.guid === o.wall_guid) ?? null;
      switch (key) {
        case 'width': {
          const lo = Math.min(o.s_start, o.s_end);
          const hi = Math.max(o.s_start, o.s_end);
          if (!(n > 0.05) || same(n, hi - lo)) return null;
          // Widened about its middle, kept on the host.
          const mid = (lo + hi) / 2;
          let a = mid - n / 2;
          let b = mid + n / 2;
          const L = host ? wallLength(host) : Infinity;
          if (a < 0) [a, b] = [0, n];
          if (b > L) [a, b] = [Math.max(0, L - n), L];
          return [{ op: 'opening.update', guid, set: { s_start: round(a), s_end: round(b) } }];
        }
        case 'opening_height':
          if (!(n > 0.05) || same(n, o.z_head - o.z_sill)) return null;
          return [{ op: 'opening.update', guid, set: { z_head: round(o.z_sill + n) } }];
        case 'sill': {
          const base = host?.z_base ?? storeyOf(model, o.storey_id)?.z_min ?? 0;
          if (!(n >= 0) || same(n, o.z_sill - base)) return null;
          const h = o.z_head - o.z_sill;
          return [{ op: 'opening.update', guid, set: { z_sill: round(base + n), z_head: round(base + n + h) } }];
        }
        case 'opening_kind':
          if (value === o.kind || !OPENING_KINDS.some((k) => k.value === value)) return null;
          return [{ op: 'opening.update', guid, set: { kind: value } }];
        default:
          return null;
      }
    }
    case 'space': {
      const s = found.row;
      const floor = storeyOf(model, s.storey_id)?.z_min ?? 0;
      switch (key) {
        case 'name': {
          const name = typeof value === 'string' ? value.trim() : '';
          if (name === '' || name === s.name) return null;
          return [{ op: 'space.update', guid, set: { name } }];
        }
        case 'space_kind':
          if (value === s.kind) return null;
          return [{ op: 'space.update', guid, set: { kind: value } }];
        case 'floor': {
          if (!Number.isFinite(n) || same(n, s.z_floor - floor)) return null;
          const zf = floor + n;
          return [{ op: 'space.update', guid, set: { z_floor: round(zf), z_ceiling: round(zf + (s.z_ceiling - s.z_floor)) } }];
        }
        case 'ceiling_height':
          if (!(n > 0.5) || same(n, s.z_ceiling - s.z_floor)) return null;
          return [{ op: 'space.update', guid, set: { z_ceiling: round(s.z_floor + n) } }];
        default:
          return null;
      }
    }
    case 'slab':
      return null;
  }
}

/**
 * ↺ on field `key`: the ops that put its detected value (from
 * `edited_from`) back, leaving every other field as edited. Null when the
 * field has no detected value or already holds it.
 */
export function resetFieldOps(model: GeometriModel, guid: string, key: FieldKey): ModelEditOp[] | null {
  const found = findElement(model, guid);
  if (!found || found.kind === 'slab') return null;
  const from = editedFrom(found.row.metadata);
  if (!from) return null;
  const field = editFields(model, found)
    .flatMap((s) => s.fields)
    .find((f) => f.key === key);
  if (!field || !field.edited) return null;
  switch (key) {
    case 'length': {
      const L = Math.hypot(num(from, 'x1')! - num(from, 'x0')!, num(from, 'y1')! - num(from, 'y0')!);
      return fieldOps(model, guid, key, L);
    }
    case 'thickness':
      return fieldOps(model, guid, key, num(from, 'thickness')!);
    case 'height':
      return fieldOps(model, guid, key, num(from, 'height')!);
    case 'external':
      return fieldOps(model, guid, key, from.is_external ? 'outer' : 'inner');
    case 'width':
      return fieldOps(model, guid, key, Math.abs(num(from, 's_end')! - num(from, 's_start')!));
    case 'opening_height':
      return fieldOps(model, guid, key, num(from, 'z_head')! - num(from, 'z_sill')!);
    case 'opening_kind':
    case 'space_kind':
      return typeof from.kind === 'string' ? fieldOps(model, guid, key, from.kind) : null;
    case 'name':
      return typeof from.name === 'string' ? fieldOps(model, guid, key, from.name) : null;
    case 'ceiling_height':
      return fieldOps(model, guid, key, num(from, 'z_ceiling')! - num(from, 'z_floor')!);
    default:
      return null;
  }
}

/** Ops for Slet: one delete per wall, opening or space (slabs are derived and skipped). Openings on a deleted wall go too. */
export function deleteOps(model: GeometriModel, guids: readonly string[]): ModelEditOp[] {
  const out: ModelEditOp[] = [];
  const seen = new Set<string>();
  const add = (op: ModelEditOp & { guid: string }) => {
    if (seen.has(op.guid)) return;
    seen.add(op.guid);
    out.push(op);
  };
  // Openings first: a wall's delete would otherwise leave them hostless for a moment.
  for (const g of guids) {
    const f = findElement(model, g);
    if (f?.kind === 'wall') for (const o of model.openings ?? []) if (o.wall_guid === g) add({ op: 'opening.delete', guid: o.guid });
  }
  for (const g of guids) {
    const f = findElement(model, g);
    if (!f || f.kind === 'slab') continue;
    add({ op: `${f.kind}.delete`, guid: g });
  }
  return out;
}

/** Whether "Tilbage til detekteret" means anything for the row: edited, or drawn by hand. */
export function isRevertable(row: { source: string; edited?: boolean; metadata: unknown }): boolean {
  return row.edited === true || editedFrom(row.metadata) !== null || row.source === 'manual';
}

/** Ops for Tilbage til detekteret over a selection (only the rows it applies to). */
export function revertOps(model: GeometriModel, guids: readonly string[]): ModelEditOp[] {
  const out: ModelEditOp[] = [];
  for (const g of guids) {
    const f = findElement(model, g);
    if (!f || f.kind === 'slab' || !isRevertable(f.row)) continue;
    out.push({ op: `${f.kind}.revert`, guid: g });
  }
  return out;
}

/** The history label of a batch ("Tykkelse · Væg 6", "Slet 3 elementer"). */
export function editLabel(action: string, model: GeometriModel, guids: readonly string[]): string {
  if (guids.length !== 1) return `${action} ${guids.length} elementer`;
  const f = findElement(model, guids[0]);
  return f ? `${action} · ${elementTitle(f)}` : action;
}

// --------------------------------------------------- properties panel --

const SOURCE_LABEL: Record<string, string> = { detected: 'Detekteret', manual: 'Manuel', imported: 'Importeret' };
const OPENING_TITLE: Record<SolidOpening['kind'], string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };

/** "Væg 6", "Dør D3", "Køkken". */
export function elementTitle(f: FoundElement): string {
  switch (f.kind) {
    case 'wall':
      return `Væg ${f.row.id}`;
    case 'opening':
      return `${OPENING_TITLE[f.row.kind]} ${f.row.kind === 'door' ? 'D' : f.row.kind === 'window' ? 'V' : 'Å'}${f.row.id}`;
    case 'space':
      return spaceLabel(f.row);
    case 'slab':
      return f.row.kind === 'roof' ? 'Tag' : 'Dæk';
  }
}

/** The floating Egenskaber panel for one selected element; null when nothing (known) is selected. */
export function propertiesView(model: GeometriModel, guid: string | null): PropertiesView | null {
  const found = findElement(model, guid);
  if (!found) return null;
  const title = elementTitle(found);
  if (found.kind === 'slab') {
    const s = found.row;
    return {
      guid: s.guid,
      kind: 'slab',
      eyebrow: s.kind === 'roof' ? 'Tag · afledt' : 'Dæk · afledt',
      title,
      chips: [{ label: 'Afledt af rum og etager', tone: 'neutral' }],
      sections: [
        {
          title: 'Mål',
          fields: [
            lengthField('thickness', 'Tykkelse', s.z_top - s.z_base, null, false),
            { key: 'area', label: 'Areal', type: 'text', value: null, display: formatAreaDa(s.area), edited: false, detected: null, editable: false },
          ],
        },
      ],
      revertable: false,
      deletable: false,
      related: [],
    };
  }
  const row = found.row as SolidWall | SolidOpening | SolidSpace;
  const edited = row.edited === true || editedFrom(row.metadata) !== null;
  const chips: PropertiesView['chips'] = [{ label: edited ? 'Redigeret' : (SOURCE_LABEL[row.source] ?? row.source), tone: edited ? 'accent' : 'neutral' }];
  if (row.locked) chips.push({ label: 'Låst', tone: 'neutral' });
  if (row.confidence !== null) chips.push({ label: `Sikkerhed ${Math.round(row.confidence * 100)} %`, tone: row.confidence < 0.6 ? 'warning' : 'success' });
  if (found.kind === 'wall' && found.row.assumed) chips.push({ label: 'Tykkelse antaget', tone: 'warning' });

  let eyebrow = '';
  let related: PropertiesView['related'] = [];
  switch (found.kind) {
    case 'wall':
      eyebrow = `Væg · ${found.row.is_external ? 'Ydervæg' : 'Indervæg'}`;
      related = (model.openings ?? [])
        .filter((o) => o.wall_guid === found.row.guid)
        .map((o) => ({ guid: o.guid, label: elementTitle({ kind: 'opening', row: o }), role: 'Åbning' }));
      break;
    case 'opening': {
      eyebrow = `Åbning · ${OPENING_TITLE[found.row.kind]}`;
      const host = model.walls?.find((w) => w.guid === found.row.wall_guid);
      related = host ? [{ guid: host.guid, label: `Væg ${host.id}`, role: 'Vært' }] : [];
      break;
    }
    case 'space':
      eyebrow = found.row.kind === 'corridor' ? 'Rum · Gang' : 'Rum';
      break;
  }
  return {
    guid: row.guid,
    kind: found.kind,
    eyebrow,
    title,
    chips,
    sections: editFields(model, found),
    revertable: isRevertable(row),
    deletable: true,
    related,
  };
}

// -------------------------------------------------- optimistic overlay --

const round = (v: number) => Math.round(v * 1e6) / 1e6;

type Row = SolidWall | SolidOpening | SolidSpace | Storey;

function markEdited<T extends Row>(row: T): T {
  return { ...row, source: 'manual', locked: true, edited: true };
}

/** Where an opening's `s` lands on its host's axis (world x/y), as the GET serves `start` / `end`. */
function onAxis(host: SolidWall | undefined, s: number): [number, number] | null {
  if (!host) return null;
  const L = wallLength(host);
  if (!(L > 1e-9)) return null;
  const t = s / L;
  return [round(host.start[0] + (host.end[0] - host.start[0]) * t), round(host.start[1] + (host.end[1] - host.start[1]) * t)];
}

function patchWall(w: SolidWall, set: Record<string, unknown>): SolidWall {
  const next = markEdited({ ...w, ...(set as Partial<SolidWall>) });
  if ('z_top' in set || 'z_base' in set) next.height = round(next.z_top - next.z_base);
  else if ('height' in set) next.z_top = round(next.z_base + next.height);
  return next;
}

/**
 * The model as it will be once `ops` are applied — what the plan, the tree
 * and Egenskaber show while the batch is on its way (and until the re-read
 * lands). Updates and deletes are applied; a create is added as sent; a
 * revert, split, merge or join waits for the server (its result is the
 * server's to compute).
 */
export function applyOps(model: GeometriModel, ops: readonly ModelEditOp[]): GeometriModel {
  if (ops.length === 0) return model;
  let walls = model.walls ? [...model.walls] : null;
  let openings = model.openings ? [...model.openings] : null;
  let spaces = model.spaces ? [...model.spaces] : null;
  let storeys = model.storeys ? [...model.storeys] : null;
  const touchedHosts = new Set<string>();
  for (const op of ops) {
    const [kind, verb] = op.op.split('.') as [string, string];
    if (verb === 'update' && 'set' in op) {
      if (kind === 'wall' && walls) {
        walls = walls.map((w) => (w.guid === op.guid ? patchWall(w, op.set) : w));
        touchedHosts.add(op.guid);
      } else if (kind === 'opening' && openings) openings = openings.map((o) => (o.guid === op.guid ? markEdited({ ...o, ...(op.set as Partial<SolidOpening>) }) : o));
      else if (kind === 'space' && spaces) spaces = spaces.map((s) => (s.guid === op.guid ? markEdited({ ...s, ...(op.set as Partial<SolidSpace>) }) : s));
      else if (kind === 'storey' && storeys) storeys = storeys.map((s) => (s.guid === op.guid ? markEdited({ ...s, ...(op.set as Partial<Storey>) }) : s));
    } else if (verb === 'delete' && 'guid' in op) {
      if (kind === 'wall' && walls) walls = walls.filter((w) => w.guid !== op.guid);
      else if (kind === 'opening' && openings) openings = openings.filter((o) => o.guid !== op.guid);
      else if (kind === 'space' && spaces) spaces = spaces.filter((s) => s.guid !== op.guid);
    } else if (verb === 'create' && 'record' in op && typeof op.record.guid === 'string') {
      const rec = { source: 'manual', locked: true, edited: true, confidence: null, metadata: null, ...op.record } as unknown;
      if (kind === 'wall' && walls) walls = [...walls, rec as SolidWall];
      else if (kind === 'opening' && openings) openings = [...openings, rec as SolidOpening];
      else if (kind === 'space' && spaces) spaces = [...spaces, rec as SolidSpace];
    }
  }
  // Openings follow their host's axis (the GET's derived `start` / `end`).
  if (openings && walls) {
    const byGuid = new Map(walls.map((w) => [w.guid, w]));
    openings = openings.map((o) => {
      if (!touchedHosts.has(o.wall_guid) && !ops.some((op) => 'guid' in op && op.guid === o.guid)) return o;
      const host = byGuid.get(o.wall_guid);
      return { ...o, start: onAxis(host, o.s_start), end: onAxis(host, o.s_end) };
    });
  }
  return { ...model, walls, openings, spaces, storeys };
}
